import { useState } from "react";
import { Button, Dialog, DropdownField, Icon, InputField, Text, View } from "../base";
import { Form, useForm } from "../base/Form";
import { SettingsList } from "../base/SettingsList";
import { SettingsListItem } from "../base/SettingsListItem";
import { useApiRequest } from "../../hooks/useApiRequest";
import { useAppDispatch, useAppSelector } from "../../redux";
import { OrganizationPhoneNumber, organizationPhoneNumberActions } from "../../redux/reducers/organizationPhoneNumberSlice";
import { OrganizationTelephony } from "../../redux/reducers/organizationTelephonySlice";
import { formatPhoneNumber } from "../../utils/strings";
import { getTelephonyProviderInfo } from "../../utils/telephony";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { useAuth } from "../../hooks/useAuth";
import { TelephonyConfigurationDropdown } from "../profile/TelephonyConfigurationDropdown";

type PhoneNumberAssignmentsDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  environmentId: string;
  onAssignmentChange?: () => void;
};

export const PhoneNumberAssignmentsDialog = ({ isOpen, onClose, agentId, environmentId, onAssignmentChange }: PhoneNumberAssignmentsDialogProps) => {
  const dispatch = useAppDispatch();
  const { isAdmin } = useAuth();
  const { organizationId } = useEffectiveOrganization();
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState<string | undefined>(undefined);

  const { fetch: updatePhoneNumber, isLoading: isUpdating } = useApiRequest<{ data: OrganizationPhoneNumber }>();
  const { fetch: configurePhoneNumber } = useApiRequest<{ data: OrganizationPhoneNumber }>();
  const { fetch: purchasePhoneNumber, isLoading: isPurchasing } = useApiRequest<{ data: OrganizationPhoneNumber }>();
  const [configuringId, setConfiguringId] = useState<string | null>(null);
  const [configureError, setConfigureError] = useState<string | null>(null);
  const [confirmConfigurePhone, setConfirmConfigurePhone] = useState<OrganizationPhoneNumber | null>(null);
  const [confirmRemovePhone, setConfirmRemovePhone] = useState<OrganizationPhoneNumber | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  const { formData: purchaseFormData, onChange: onChangePurchase } = useForm<{
    telephonyId: string;
    areaCode: string;
    countryCode: string;
    friendlyName: string;
  }>({
    telephonyId: "",
    areaCode: "",
    countryCode: "US",
    friendlyName: "",
  });

  const resetPurchaseForm = () => {
    onChangePurchase({
      telephonyId: "",
      areaCode: "",
      countryCode: "US",
      friendlyName: "",
    });
    setPurchaseError(null);
  };

  const onClickPurchase = async () => {
    setPurchaseError(null);
    const { error } = await purchasePhoneNumber(`${phoneNumberBasePath}/purchase`, {
      method: "POST",
      body: {
        telephonyId: purchaseFormData.telephonyId || "voicerun",
        areaCode: purchaseFormData.areaCode || "",
        countryCode: purchaseFormData.countryCode || "US",
        friendlyName: purchaseFormData.friendlyName || "",
      },
    });
    if (error) {
      setPurchaseError(error.message || "Failed to purchase phone number. Please try again.");
    } else {
      setShowPurchaseForm(false);
      resetPurchaseForm();
      refreshPhoneNumbers();
    }
  };

  const orgPhoneNumbers = useAppSelector((state) => Object.values(state.organizationPhoneNumbers.entities)) as OrganizationPhoneNumber[];
  const telephonyConfigs = useAppSelector((state) => Object.values(state.organizationTelephony.entities)) as OrganizationTelephony[];

  const getProviderForPhone = (phoneNumber: OrganizationPhoneNumber) => {
    const config = telephonyConfigs.find((c) => c.id === phoneNumber.telephonyId);
    return config ? getTelephonyProviderInfo(config.providerType) : null;
  };

  const isVoiceRunProvider = (phoneNumber: OrganizationPhoneNumber) => {
    const config = telephonyConfigs.find((c) => c.id === phoneNumber.telephonyId);
    return !config || !["twilio", "telnyx"].includes(config.providerType);
  };

  const phoneNumberBasePath = `/v1/organizations/${organizationId}/phoneNumbers`;

  const refreshPhoneNumbers = async () => {
    if (organizationId) {
      await dispatch(organizationPhoneNumberActions.get({ organizationId }) as any);
      onAssignmentChange?.();
    }
  };

  // Numbers assigned to the current environment
  const assignedNumbers = orgPhoneNumbers.filter((pn) => pn.agentEnvironmentId === environmentId);

  // Available numbers = those not assigned to any environment
  const availableNumbers = orgPhoneNumbers.filter((pn) => !pn.agentEnvironmentId);

  const dropdownOptions = availableNumbers.map((pn) => {
    const provider = getProviderForPhone(pn);
    return {
      label: (
        <View className="flex items-center gap-2">
          {provider && (
            <Icon
              name={provider.icon}
              size={16}
              className="text-gray-500 dark:text-gray-400 shrink-0"
            />
          )}
          <Text className="text-gray-900 dark:text-gray-100">
            {formatPhoneNumber(pn.phoneNumber)}
            {pn.friendlyName ? ` — ${pn.friendlyName}` : ""}
          </Text>
        </View>
      ),
      value: pn.id,
      searchValue: `${pn.phoneNumber} ${pn.friendlyName || ""} ${provider?.name || ""}`,
    };
  });

  const onAssign = async () => {
    if (!selectedPhoneNumberId) return;
    setIsAssigning(true);
    const { error } = await updatePhoneNumber(`${phoneNumberBasePath}/${selectedPhoneNumberId}`, {
      method: "PATCH",
      body: { agentId, agentEnvironmentId: environmentId },
    });
    if (!error) {
      await configurePhoneNumber(`${phoneNumberBasePath}/${selectedPhoneNumberId}/configure`, { method: "POST" });
    }
    setSelectedPhoneNumberId(undefined);
    await refreshPhoneNumbers();
    setIsAssigning(false);
  };

  const onRemove = async () => {
    if (!confirmRemovePhone) return;
    setIsRemoving(true);
    await configurePhoneNumber(`${phoneNumberBasePath}/${confirmRemovePhone.id}/unconfigure`, { method: "POST" });
    await updatePhoneNumber(`${phoneNumberBasePath}/${confirmRemovePhone.id}`, {
      method: "PATCH",
      body: { agentId: null, agentEnvironmentId: null },
    });
    setConfirmRemovePhone(null);
    await refreshPhoneNumbers();
    setIsRemoving(false);
  };

  const onConfigure = async () => {
    if (!confirmConfigurePhone) return;
    const phoneId = confirmConfigurePhone.id;
    setConfiguringId(phoneId);
    setConfigureError(null);
    const { error } = await configurePhoneNumber(`${phoneNumberBasePath}/${phoneId}/configure`, { method: "POST" });
    setConfiguringId(null);
    if (error) {
      setConfigureError(error.message || "Failed to configure phone number");
    } else {
      setConfirmConfigurePhone(null);
    }
  };

  const confirmProvider = confirmConfigurePhone ? getProviderForPhone(confirmConfigurePhone) : null;
  const confirmRemoveProvider = confirmRemovePhone ? getProviderForPhone(confirmRemovePhone) : null;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        confirmRemovePhone
          ? "Remove Phone Number"
          : confirmConfigurePhone
            ? "Configure Phone Number"
            : showPurchaseForm
              ? "Purchase New Number"
              : "Phone Number Assignments"
      }
      size="md"
    >
      {confirmRemovePhone ? (
        <>
          <View className="flex flex-col p-4 gap-3">
            <Text className="text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to remove this phone number from this environment? Incoming calls will no longer be routed to this agent.
            </Text>
            <View className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              {confirmRemoveProvider && (
                <Icon
                  name={confirmRemoveProvider.icon}
                  size={16}
                  className="text-gray-500 dark:text-gray-400 shrink-0"
                />
              )}
              <View className="flex flex-col">
                <Text className="text-sm font-medium text-gray-900 dark:text-white">{formatPhoneNumber(confirmRemovePhone.phoneNumber)}</Text>
                {(confirmRemovePhone.friendlyName || confirmRemoveProvider) && (
                  <Text className="text-xs text-gray-500 dark:text-gray-400">
                    {[confirmRemovePhone.friendlyName, confirmRemoveProvider?.name].filter(Boolean).join(" · ")}
                  </Text>
                )}
              </View>
            </View>
          </View>
          <View className="flex items-center justify-end p-4 gap-2 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="neutral"
              size="auto"
              onClick={() => setConfirmRemovePhone(null)}
              disabled={isRemoving}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="auto"
              onClick={onRemove}
              disabled={isRemoving}
            >
              <Icon
                name={isRemoving ? "loader" : "close"}
                size={14}
                className={isRemoving ? "animate-spin" : ""}
              />
              {isRemoving ? "Removing..." : "Remove"}
            </Button>
          </View>
        </>
      ) : confirmConfigurePhone ? (
        <>
          <View className="flex flex-col p-4 gap-3">
            <Text className="text-sm text-gray-600 dark:text-gray-400">
              This will update the telephony provider to route incoming calls for this number to this agent environment.
            </Text>
            <View className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              {confirmProvider && (
                <Icon
                  name={confirmProvider.icon}
                  size={16}
                  className="text-gray-500 dark:text-gray-400 shrink-0"
                />
              )}
              <View className="flex flex-col">
                <Text className="text-sm font-medium text-gray-900 dark:text-white">{formatPhoneNumber(confirmConfigurePhone.phoneNumber)}</Text>
                {(confirmConfigurePhone.friendlyName || confirmProvider) && (
                  <Text className="text-xs text-gray-500 dark:text-gray-400">
                    {[confirmConfigurePhone.friendlyName, confirmProvider?.name].filter(Boolean).join(" · ")}
                  </Text>
                )}
              </View>
            </View>
            {configureError && (
              <View className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                <Icon
                  name="warning-amber"
                  size={14}
                  className="text-red-500 dark:text-red-400 shrink-0"
                />
                <Text className="text-sm text-red-600 dark:text-red-400">{configureError}</Text>
              </View>
            )}
          </View>
          <View className="flex items-center justify-end p-4 gap-2 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="neutral"
              size="auto"
              onClick={() => {
                setConfigureError(null);
                setConfirmConfigurePhone(null);
              }}
              disabled={!!configuringId}
            >
              Back
            </Button>
            <Button
              variant="info"
              size="auto"
              onClick={onConfigure}
              disabled={!!configuringId}
            >
              <Icon
                name={configuringId ? "loader" : "settings"}
                size={14}
                className={configuringId ? "animate-spin" : ""}
              />
              {configuringId ? "Configuring..." : "Configure"}
            </Button>
          </View>
        </>
      ) : showPurchaseForm ? (
        <View className="flex flex-col">
          <View className="p-4 space-y-4">
            <Form
              data={purchaseFormData}
              onChange={onChangePurchase}
            >
              <TelephonyConfigurationDropdown
                value={purchaseFormData.telephonyId}
                onChange={(value) => onChangePurchase({ telephonyId: value })}
                organizationId={organizationId}
                placeholder="Select a telephony configuration"
              />

              <InputField
                name="friendlyName"
                label="Friendly Name"
                placeholder="Enter a friendly name (optional)"
              />

              <InputField
                name="areaCode"
                label="Area Code"
                placeholder="e.g. 415"
              />

              <InputField
                name="countryCode"
                label="Country Code"
                placeholder="e.g. US"
              />

              {purchaseError && (
                <View className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800">
                  <Icon
                    name="error"
                    size={14}
                    className="text-red-500 dark:text-red-400 shrink-0"
                  />
                  <Text className="text-xs text-red-600 dark:text-red-400">{purchaseError}</Text>
                </View>
              )}
            </Form>
          </View>
          <View className="flex items-center justify-end p-4 gap-2 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="neutral"
              size="auto"
              onClick={() => {
                setShowPurchaseForm(false);
                resetPurchaseForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              size="auto"
              onClick={onClickPurchase}
              disabled={isPurchasing}
            >
              {isPurchasing ? (
                <Icon
                  name="loader"
                  size={14}
                  className="animate-spin"
                />
              ) : (
                <Icon
                  name="add"
                  size={14}
                />
              )}
              Purchase
            </Button>
          </View>
        </View>
      ) : (
        <View className="flex flex-col gap-4 p-4 max-h-[70vh] overflow-y-auto">
          <Text className="text-sm text-gray-600 dark:text-gray-400">
            Assign organization phone numbers to this environment. Incoming calls to assigned numbers will be routed to this agent environment.
          </Text>

          {/* Assign new number */}
          <View className="flex flex-row items-end gap-2">
            <View className="flex-1">
              <DropdownField
                label="Add Phone Number"
                options={dropdownOptions}
                value={selectedPhoneNumberId}
                onChange={setSelectedPhoneNumberId}
                placeholder={availableNumbers.length === 0 ? "No available numbers" : "Select a phone number"}
                readOnly={availableNumbers.length === 0}
              />
            </View>
            <Button
              variant="success"
              size="auto"
              onClick={onAssign}
              disabled={!selectedPhoneNumberId || isAssigning}
              className="mb-[1px]"
            >
              <Icon
                name={isAssigning ? "loader" : "add"}
                size={14}
                className={isAssigning ? "animate-spin" : ""}
              />
              {isAssigning ? "Assigning..." : "Assign"}
            </Button>
          </View>

          {/* Purchase new number link */}
          <button
            onClick={() => setShowPurchaseForm(true)}
            className="flex flex-row items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors -mt-1"
          >
            <Icon
              name="add"
              size={12}
            />
            Purchase new number
          </button>

          {/* Current assignments */}
          <View className="flex flex-col gap-2">
            <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Assigned Numbers</Text>
            {assignedNumbers.length === 0 ? (
              <Text className="text-sm text-gray-500 dark:text-gray-400 py-2">No phone numbers assigned to this environment.</Text>
            ) : (
              <SettingsList>
                {assignedNumbers.map((pn) => {
                  const provider = getProviderForPhone(pn);
                  return (
                    <SettingsListItem
                      key={pn.id}
                      icon={provider?.icon || "phone"}
                      title={formatPhoneNumber(pn.phoneNumber)}
                      subtitle={
                        <View className="flex items-center gap-1.5">
                          {pn.friendlyName && <Text className="text-sm text-gray-500 dark:text-gray-400">{pn.friendlyName}</Text>}
                          {provider && <Text className="text-xs text-gray-400 dark:text-gray-500">{provider.name}</Text>}
                        </View>
                      }
                      right={
                        <View className="flex items-center gap-1.5">
                          <button
                            onClick={() => {
                              setConfigureError(null);
                              setConfirmConfigurePhone(pn);
                            }}
                            disabled={configuringId === pn.id}
                            className="flex flex-row items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors border bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Icon
                              name={configuringId === pn.id ? "loader" : "settings"}
                              size={12}
                              className={configuringId === pn.id ? "animate-spin" : ""}
                            />
                            {configuringId === pn.id ? "Configuring..." : "Configure"}
                          </button>
                          {(isAdmin || !isVoiceRunProvider(pn)) && (
                            <button
                              onClick={() => setConfirmRemovePhone(pn)}
                              className="flex flex-row items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors border bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/50"
                            >
                              <Icon
                                name="close"
                                size={12}
                              />
                              Remove
                            </button>
                          )}
                        </View>
                      }
                    />
                  );
                })}
              </SettingsList>
            )}
          </View>
        </View>
      )}
    </Dialog>
  );
};
